// Global Instructions Rule Applied!
import { useState, useEffect, useCallback } from 'react'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import { searchDealsForPopup } from '../../app_modules/deals/services/dealsService.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'

/** @type {import('../../state_manager/data_models/select_list_entry.ts').SelectListEntry[]} */
const NO_DEALS = []

const BACKEND_BASE_URL = getBackendBaseUrl()

export const useDealSearch = (debounceMs = 300) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(NO_DEALS)
  const [searchTerm, setSearchTerm] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  const searchDeals = useCallback(
    async (query) => {
      const trimmedQuery = query?.trim() || ''
      setSearchTerm(trimmedQuery)

      if (!trimmedQuery || trimmedQuery.length < 2) {
        setResults([])
        setLoading(false)
        setError(null)
        setHasSearched(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const currentToken = getAccessToken(store.getState().auth)
        const response = await searchDealsForPopup(trimmedQuery, 20, currentToken)

        if (response.error) {
          throw new Error(response.error)
        }

        if (trimmedQuery === searchTerm || searchTerm === '') {
          const formattedResults = (response.data || []).map((deal) => ({
            label: deal.deals__name || '',
            value: deal.deals__id,
            name: deal.deals__name || ''
          }))
          setResults(formattedResults)
          setHasSearched(true)
        }
      } catch (error) {
        console.error('Error searching deals:', error)
        setError(error.message || 'An unexpected error occurred while searching deals')
        setResults([])
        setHasSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [searchTerm]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchDeals(searchTerm)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, searchDeals, debounceMs])

  const clearResults = useCallback(() => {
    setResults([])
    setSearchTerm('')
    setError(null)
    setLoading(false)
    setHasSearched(false)
  }, [])

  const triggerSearch = useCallback(
    (query) => {
      setSearchTerm(query)
      searchDeals(query)
    },
    [searchDeals]
  )

  return {
    searchDeals: triggerSearch,
    loading,
    error,
    results,
    clearResults,
    hasSearched
  }
}
